import type { FirmContactView, FirmContactCreate } from "../interfaces/models/IFirmContact";
import { getJurisdictionLabel } from "./jurisdiction/structuredJurisdiction";

export type ContactDropdownPerson = {
  id: string;
  name: string;
  isAttorney?: unknown;
  mainOffice?: string;
  additionalOffices?: string[];
  [key: string]: unknown;
};

export type FirmContactSavePayload = FirmContactCreate & { id?: string };

export function firmContactBlockKey(
  contact: Pick<FirmContactView, "id" | "firmId" | "officeId">,
): string {
  return String(contact.id?.trim() || `${contact.firmId}-${contact.officeId}`);
}

function hasDuplicateIds(ids: string[]): boolean {
  const seen = new Set<string>();
  for (const id of ids.map((value) => String(value ?? "").trim()).filter(Boolean)) {
    if (seen.has(id)) {
      return true;
    }
    seen.add(id);
  }
  return false;
}

export function toFirmContactPayload(
  contact: FirmContactView,
  persistedId?: string,
): FirmContactSavePayload {
  const payload: FirmContactSavePayload = {
    firmId: contact.firmId,
    officeId: contact.officeId,
    primaryContactId: contact.primaryContact?.id ?? "",
    secondaryContactId: contact.secondaryContact?.id ?? "",
    accountingContactId: contact.accountingContact?.id ?? "",
    marketingContactIds: (contact.marketingContacts ?? [])
      .map((m) => m.id)
      .filter(Boolean),
    itContactId: contact.itContact?.id || undefined,
    socialMediaContactId: contact.socialMediaContact?.id || undefined,
    hrContactId: contact.hrContact?.id || undefined,
    liaisonContactIds: (contact.liaisonContacts ?? [])
      .map((l) => l.id)
      .filter(Boolean),
    ccContactId: contact.ccContact?.id || undefined,
  };

  const id = String(persistedId ?? contact.id ?? "").trim();
  if (id) {
    payload.id = id;
  }

  return payload;
}

export function serializeFirmContactPayload(
  payload: FirmContactSavePayload,
): string {
  return JSON.stringify({
    firmId: payload.firmId,
    officeId: payload.officeId,
    primaryContactId: payload.primaryContactId,
    secondaryContactId: payload.secondaryContactId,
    accountingContactId: payload.accountingContactId,
    marketingContactIds: payload.marketingContactIds ?? [],
    itContactId: payload.itContactId ?? "",
    socialMediaContactId: payload.socialMediaContactId ?? "",
    hrContactId: payload.hrContactId ?? "",
    liaisonContactIds: payload.liaisonContactIds ?? [],
    ccContactId: payload.ccContactId ?? "",
  });
}

export type ValidateFirmContactBlockOptions = {
  /** When true, Primary/Secondary/Accounting must be assigned. */
  requireCoreContacts?: boolean;
};

export function validateFirmContactBlock(
  contact: FirmContactView,
  options?: ValidateFirmContactBlockOptions,
): string | null {
  const label =
    getJurisdictionLabel({ jurisdiction: contact.jurisdiction }) || "this office";
  const requireCoreContacts = options?.requireCoreContacts ?? true;

  if (requireCoreContacts) {
    if (!String(contact.primaryContact?.id ?? "").trim()) {
      return `Primary Contact is required for ${label}.`;
    }
    if (!String(contact.secondaryContact?.id ?? "").trim()) {
      return `Secondary Contact is required for ${label}.`;
    }
    if (!String(contact.accountingContact?.id ?? "").trim()) {
      return `Accounting Contact is required for ${label}.`;
    }
  }

  const marketingIds = (contact.marketingContacts ?? [])
    .map((m) => m.id)
    .filter(Boolean);
  if (marketingIds.length > 5) {
    return `Marketing Contact allows up to 5 contacts for ${label}.`;
  }
  if (hasDuplicateIds(marketingIds)) {
    return `Marketing Contact cannot include the same person more than once for ${label}.`;
  }

  const liaisonIds = (contact.liaisonContacts ?? [])
    .map((l) => l.id)
    .filter(Boolean);
  if (liaisonIds.length > 5) {
    return `Liaison Contact allows up to 5 contacts for ${label}.`;
  }
  if (hasDuplicateIds(liaisonIds)) {
    return `Liaison Contact cannot include the same person more than once for ${label}.`;
  }

  return null;
}

export const ATTORNEY_ONLY_FIRM_CONTACT_TYPES = new Set([
  "primaryContact",
  "secondaryContact",
]);

export const emptyContactSelection = (): ContactDropdownPerson => ({
  id: "",
  name: "",
});

/** Dropdown options need a person id and a non-empty display name. */
export function hasDisplayableContactName(
  contact: Pick<ContactDropdownPerson, "id" | "name">,
): boolean {
  return (
    Boolean(String(contact.id ?? "").trim()) &&
    Boolean(String(contact.name ?? "").trim())
  );
}

export function filterContactsWithDisplayName(
  contacts: ContactDropdownPerson[],
): ContactDropdownPerson[] {
  return contacts.filter(hasDisplayableContactName);
}

/** Cosmos/API may send `isAttorney` as boolean, string, or number. */
export function isPersonAttorney(person: { isAttorney?: unknown }): boolean {
  const value = person?.isAttorney;
  if (value === true || value === 1) {
    return true;
  }
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "true" || normalized === "yes" || normalized === "1";
  }
  return false;
}

export function filterAttorneysForContactDropdown(
  contacts: ContactDropdownPerson[]
): ContactDropdownPerson[] {
  return contacts.filter(isPersonAttorney);
}

/** Person is linked to an office via mainOffice or additionalOffices. */
export function personBelongsToOffice(
  person: { mainOffice?: string; additionalOffices?: string[] },
  officeId: string,
): boolean {
  const oid = String(officeId ?? "").trim();
  if (!oid) {
    return false;
  }
  if (String(person.mainOffice ?? "").trim() === oid) {
    return true;
  }
  return (person.additionalOffices ?? []).some(
    (id) => String(id ?? "").trim() === oid,
  );
}

/** Primary/secondary: attorneys only; all other roles: everyone at the firm. */
export function contactsForFirmContactDropdown(
  allContacts: ContactDropdownPerson[],
  contactType: string,
  selectedPersonId?: string | null,
  selectedLookupSource?: ContactDropdownPerson[],
): ContactDropdownPerson[] {
  const withNames = filterContactsWithDisplayName(allContacts);
  const filtered = ATTORNEY_ONLY_FIRM_CONTACT_TYPES.has(contactType)
    ? filterAttorneysForContactDropdown(withNames)
    : withNames;

  if (!selectedPersonId) {
    return filtered;
  }

  const lookup = selectedLookupSource ?? allContacts;
  const selected = lookup.find((contact) => contact.id === selectedPersonId);
  if (!selected || filtered.some((contact) => contact.id === selectedPersonId)) {
    return filtered;
  }

  // Keep an existing selection visible even if it no longer matches the filter.
  if (hasDisplayableContactName(selected)) {
    return [selected, ...filtered];
  }
  return filtered;
}

/** Firm-contact dropdown scoped to professionals linked to a specific office. */
export function contactsForOfficeContactDropdown(
  firmPersons: ContactDropdownPerson[],
  officeId: string,
  contactType: string,
  selectedPersonId?: string | null,
): ContactDropdownPerson[] {
  const officePersons = firmPersons.filter((person) =>
    personBelongsToOffice(person, officeId),
  );
  return contactsForFirmContactDropdown(
    officePersons,
    contactType,
    selectedPersonId,
    firmPersons,
  );
}

export function formatPersonsForContactDropdown(
  persons: unknown[] | undefined | null
): ContactDropdownPerson[] {
  if (!persons?.length) {
    return [];
  }
  return filterContactsWithDisplayName(
    persons.map((person: any) => ({
      ...person,
      name: `${person.name ?? ""} ${person.lastName ?? ""}`.trim(),
    })),
  );
}

export function resolveContactSelection(
  contacts: ContactDropdownPerson[],
  personId?: string | null
): ContactDropdownPerson {
  if (!personId) {
    return emptyContactSelection();
  }
  return contacts.find((contact) => contact.id === personId) ?? emptyContactSelection();
}

type FirmContactRecord = {
  jurisdictions?: { jurisdiction?: string; contacts?: ContactEntry[] }[];
  contacts?: ContactEntry[];
};

type ContactEntry = {
  type?: string;
  contact?: string;
  name?: string;
};

function getActiveContactEntries(
  record: FirmContactRecord,
  firmJurisdiction?: string,
): ContactEntry[] {
  if (record.jurisdictions?.length) {
    const match =
      record.jurisdictions.find((j) => j.jurisdiction === firmJurisdiction) ??
      record.jurisdictions[0];
    return match?.contacts ?? [];
  }
  return record.contacts ?? [];
}

function pickContactEntry(
  contacts: ContactEntry[],
  type: string,
): { id: string; name: string } | null {
  const item = contacts.find((entry) => entry.type === type);
  const id = String(item?.contact ?? "").trim();
  if (!id) {
    return null;
  }
  return {
    id,
    name: String(item?.name ?? "").trim(),
  };
}

export type FirmContactValidation = {
  hasPrimaryContact: boolean;
  primaryContactId: string;
  primaryContactName: string;
  hasAccountingContact: boolean;
  accountingContactId: string;
  accountingContactName: string;
  primaryAndAccountingSamePerson: boolean;
  canComparePrimaryAndAccounting: boolean;
};

export const EMPTY_FIRM_CONTACT_VALIDATION: FirmContactValidation = {
  hasPrimaryContact: false,
  primaryContactId: "",
  primaryContactName: "",
  hasAccountingContact: false,
  accountingContactId: "",
  accountingContactName: "",
  primaryAndAccountingSamePerson: false,
  canComparePrimaryAndAccounting: false,
};

export function resolveContactValidationFromView(
  contact?: FirmContactView | null,
): FirmContactValidation {
  if (!contact) {
    return EMPTY_FIRM_CONTACT_VALIDATION;
  }

  const primaryId = String(contact.primaryContact?.id ?? "").trim();
  const primaryName = String(contact.primaryContact?.fullName ?? "").trim();
  const accountingId = String(contact.accountingContact?.id ?? "").trim();
  const accountingName = String(contact.accountingContact?.fullName ?? "").trim();
  const canCompare = Boolean(primaryId && accountingId);

  return {
    hasPrimaryContact: Boolean(primaryId),
    primaryContactId: primaryId,
    primaryContactName: primaryName,
    hasAccountingContact: Boolean(accountingId),
    accountingContactId: accountingId,
    accountingContactName: accountingName,
    canComparePrimaryAndAccounting: canCompare,
    primaryAndAccountingSamePerson: canCompare
      ? primaryId === accountingId
      : false,
  };
}

export function resolveFirmContactValidation(
  firmContactData: FirmContactRecord | FirmContactRecord[] | null | undefined,
  firmJurisdiction?: string,
): FirmContactValidation {
  const record = Array.isArray(firmContactData)
    ? firmContactData[0]
    : firmContactData;
  if (!record) {
    return EMPTY_FIRM_CONTACT_VALIDATION;
  }

  const contacts = getActiveContactEntries(record, firmJurisdiction);
  const primary = pickContactEntry(contacts, "primaryContact");
  const accounting = pickContactEntry(contacts, "accountingContact");
  const canCompare = Boolean(primary && accounting);

  return {
    hasPrimaryContact: Boolean(primary),
    primaryContactId: primary?.id ?? "",
    primaryContactName: primary?.name ?? "",
    hasAccountingContact: Boolean(accounting),
    accountingContactId: accounting?.id ?? "",
    accountingContactName: accounting?.name ?? "",
    canComparePrimaryAndAccounting: canCompare,
    primaryAndAccountingSamePerson: canCompare
      ? primary!.id === accounting!.id
      : false,
  };
}
